import React, { useState, useEffect, useRef } from 'react';
import Select from "react-select";
import { Loader } from 'lucide-react';
import Spacing from '../Spacing';
import { can_visit, getSecureData, notify } from "../../helpers";
import { AddDocflowCategory, UpdateDocflowCategory } from '../../services/aiCharting';
import { Drawer } from 'vaul';

export default function AddAndEditDocflowCategoryModal(props) {
  const {    
    account_id,
    location_id,
    reloadData = () => {},
    reloadDataFilter = {},
    activeModal = false,
    setActiveModal = () => {},
    editValue = {},
    editModal = false,
    previousScrollPosition = 0
  } = props;

  const defaultValues = {
    account_id: account_id,
    location_id: location_id,
    ...(editModal && { id: editValue.id }),
    name: editValue?.name ? editValue?.name : "",
    status: editValue?.status ? editValue?.status : "Active",
    practice_ids: editValue?.practice_ids ? editValue?.practice_ids : [],
    prompt_instructions: editValue?.prompt_instructions ? editValue?.prompt_instructions : "",
    everyone: editValue?.practice_ids?.length > 0 ? false : true
  }

  const [formData, setFormData] = useState(defaultValues);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [allPractice, setAllPractice] = useState([]);
  const dropdownRef = useRef(null);

  const getAllPractices = async () => {
    const accounts_names= await getSecureData("account_names");

    const allPractices = Object.entries(accounts_names).map(([key, value]) => ({
      value: parseInt(key, 10),
      label: value,
    }));
    setAllPractice(allPractices);
  };

  useEffect(() => {
    getAllPractices();
  }, []);

  const handlePracticeChange = (selectedOption) => {
    const selectedValues = selectedOption.map((option) => option.value);
    setFormData({ ...formData, practice_ids: selectedValues, everyone: false });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleAddSubmitAction = async (e) => {
    e.preventDefault();
    setSubmitLoader(true);

    try {
      const response = await AddDocflowCategory(formData);
    
      if (response.detail) {
        notify('error', response.detail, 2);
      } else { 
        if(response?.data?.status === "success") {
          notify("success", response.data.message, 2);
          setFormData(defaultValues);
          reloadData(reloadDataFilter);     
          handleDrawerClose();
        } else {
          notify("error", response?.data?.message, 2);
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 2);
      }
    } finally {
      setSubmitLoader(false);
    }
  };

  const handleUpdateSubmitAction = async (e) => {
    e.preventDefault();
    setSubmitLoader(true);

    try {
      const response = await UpdateDocflowCategory(formData);
    
      if (response.detail) {
        notify('error', response.detail, 2);
      } else { 
        if(response?.data?.status === "success") {
          notify("success", response.data.message, 2);
          setFormData(defaultValues);
          reloadData(reloadDataFilter);     
          handleDrawerClose();
        } else {
          notify("error", response?.data?.message, 2);
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 2);
      }
    } finally {
      setSubmitLoader(false);
    }
  };

  const onSubmitHandler = editModal ? handleUpdateSubmitAction : handleAddSubmitAction;

  const handleDrawerClose = () => {
    setActiveModal(false);
    window.scrollTo(0, previousScrollPosition);
  };

  return (
    <div style={{ height: "100%" }} ref={dropdownRef}>
      <Drawer.Root open={activeModal} onOpenChange={handleDrawerClose}>
        <Drawer.Trigger className="docpro_single_patiant_modal_btn">
        </Drawer.Trigger>
        <Drawer.Portal>
          <Drawer.Overlay className="docpro_drawer_overlay"/>
          <Drawer.Content className="docpro_drawer_content docpro_drawer_style_4">
            <Drawer.Title className="sr-only">Patient Details</Drawer.Title>
            <Drawer.Description className="sr-only">View details of a parient</Drawer.Description>
            <div className="docpro_modal_wrap docpro_style_1 docpro_modal_lg">

              <div className="docpro_modal" style={{ position: 'unset' }}>
                <div className="docpro_signout_wrap">
                  <form onSubmit={onSubmitHandler}> 
                    <div className="docpro_signout">
                      <h2 className="docpro_fs_24 docpro_mb_0">{editModal ? "Edit" : "Add New"} Docflow Template </h2>
                      <Spacing lg="25" sm="25" />

                      <div className='w-full'>
                        <div className="docpro_grid docpro_grid_gap_20 docpro_grid_col_3_1">
                          <div className="docpro_grid_item">
                            <label className="docpro_fw_500 docpro_primary_color">
                              Name <span className="docpro_red_color">*</span>
                            </label>
                            <input type="text" className="docpro_form_field size_lg" name='name' required onChange={handleChange} value={formData?.name} />
                          </div>
                          <div className="docpro_grid_item">
                            <label className="docpro_fw_500 docpro_primary_color">
                              Status
                            </label>
                            <div className="docpro_radio_group">
                              <div className="docpro_radio">
                                <input
                                  type="radio"
                                  name="status"
                                  onChange={handleChange}
                                  defaultChecked={formData?.status === "Active"}
                                  value="Active"
                                />
                                <label style={{ paddingLeft: "30px" }}>Active</label>
                              </div>
                              <div className="docpro_radio">
                                <input
                                  type="radio"
                                  name="status"
                                  onChange={handleChange}
                                  defaultChecked={formData?.status === "Deactive"}
                                  value="Deactive"
                                />
                                <label style={{ paddingLeft: "30px" }}>Deactive</label>
                              </div>
                            </div>
                          </div>
                        </div>

                        <Spacing lg="20" sm="20" />
                        <div>          
                          <label className="docpro_fw_500 docpro_primary_color"> Select Practice </label>
                          <Select
                            isMulti
                            name="practice_ids"
                            options={allPractice}
                            placeholder="Search..."
                            isSearchable
                            value={allPractice?.filter((option) => 
                              formData.practice_ids?.includes(option.value)
                            ) || []}
                            onChange={handlePracticeChange}
                            className="react-select-container w-full"
                            classNamePrefix="react-select"
                            filterOption={(option, rawInput) => {
                              const words = rawInput.toLowerCase().split(" ");
                              return words.every((word) =>
                                option.label.toLowerCase().includes(word)
                              );
                            }}
                            styles={{
                              control: (base) => ({
                                ...base,
                                minHeight: '40px',
                                borderColor: 'var(--border)',
                                borderRadius: '8px'
                              }),
                              menu: (base) => ({
                                ...base,
                                zIndex: 1050,
                                marginTop: '-2px',
                                borderRadius: '0 0 3px 3px',
                              }),
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 1050,
                                padding: 0
                              }),
                            }}
                          />
                        </div>
                        <Spacing lg="20" sm="20" />

                        <div>
                          <label className="docpro_fw_500 docpro_primary_color">
                            Prompt Instructions <span className="docpro_red_color">*</span>
                          </label>
                          <textarea
                            required
                            className="docpro_form_field size_lg"
                            cols="30"
                            rows="20"
                            name='prompt_instructions'
                            onChange={handleChange}
                            value={formData?.prompt_instructions}
                          ></textarea>
                        </div>
                      </div>

                      <div className='text-right'>
                        <Spacing lg="35" sm="35" />
                        <div className='flex items-center docpro_grid_gap_10' style={{ justifyContent: "end" }}>
                          <button
                            type='button'
                            className="docpro_bordered_btn docpro_fw_600 size_lg"
                            onClick={() => handleDrawerClose()}
                          >
                            Cancel
                          </button>
                          {(can_visit("administrator") && can_visit("administrator_docflow_template")) && (
                            <button
                              type='submit'
                              className="docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg"
                            >
                              {editModal ?
                                <span>Update</span>
                              :
                                <span>Save</span>
                              }
                              {submitLoader && (
                                <span className="docpro_loading">
                                  <Loader width={20} height={20} />
                                </span>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </form>

                  <div className="docpro_invoice_btns docpro_hide_print">
                    <button
                      type='button'
                      className="docpro_invoice_btn docpro_color3 close_button"
                      onClick={() => handleDrawerClose()}
                    >
                      <span className="docpro_btn_icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                      </span>
                      <span className="docpro_btn_text">Close</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
}
